import { initMenu } from '../components/menu.js';
import { initBanner } from '../components/banner.js';
import { initFooter } from '../components/footer.js';
import { songs } from '../middleware/songs.js';

const categories = [
  { key: "accueil", value: "Accueil des invités" },
  { key: "ceremonie", value: "Cérémonie" },
  { key: "vindhonneur", value: "Thé d'honneur" },
  { key: "repas", value: "Repas" },
  { key: "fun", value: "Fun / Surprise" },
  { key: "danses", value: "Danses (ouverture de bal)" },
  { key: "soireedansante", value: "Soirée dansante" },
  { key: "afterparty", value: "After Party" },
  { key: "hommage", value: "Hommage / Souvenirs" },
  { key: "misc", value: "Autre" },
];

async function getYoutubeVideoTitle(videoId) {
  const apiKey = process.env.YOUTUBE_API_KEY;
  const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${apiKey}`;

  try {
    const response = await fetch(url);
    const data = await response.json();
    return data.items && data.items.length > 0 ? data.items[0].snippet.title : null;
  } catch (error) {
    console.error('Erreur YouTube API:', error);
    return null;
  }
}

function extractYoutubeId(url) {
  const regex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return url.match(regex)?.[1] || null;
}

function extractSpotifyId(url) {
  const trackId = url.split("/").pop();
  return trackId.length === 22 ? trackId : null;
}

function extractSoundCloudId(url) {
  const regex = /https:\/\/soundcloud\.com\/([^\/]+)\/([^\/]+)/;
  const match = url.match(regex);
  return match ? { user: match[1], track: match[2] } : null;
}

function extractDeezerId(url) {
  const regex = /https:\/\/www\.deezer\.com\/(?:[a-z]{2}\/)?track\/([0-9]+)/;
  return url.match(regex)?.[1] || null;
}

async function getSpotifyAccessToken() {
  const clientId = process.env.SPOTIFY_CLIENT_ID;
  const clientSecret = process.env.SPOTIFY_CLIENT_SECRET;
  const credentials = btoa(`${clientId}:${clientSecret}`);

  try {
    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({ 'grant_type': 'client_credentials' })
    });
    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Erreur Spotify Token:', error);
    return null;
  }
}

async function getSpotifyTrackData(trackId) {
  const token = await getSpotifyAccessToken();
  if (!token) return null;

  const url = `https://api.spotify.com/v1/tracks/${trackId}`;

  try {
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const trackData = await response.json();
    return {
      trackTitle: trackData.name,
      trackImage: trackData.album.images[0].url,
    };
  } catch (error) {
    console.error('Erreur Spotify API:', error);
    return null;
  }
}

async function handleAddMedia() {
  const mediaUrl = document.getElementById('videoUrl').value.trim();

  if (!mediaUrl) {
    alert("Veuillez entrer une URL.");
    return;
  }

  const mediaDetails = await fetchMediaDetails(mediaUrl);
  if (!mediaDetails) {
    alert("URL invalide ou plateforme non supportée !");
    return;
  }

  const newSong = await songs.addSong(mediaDetails);
  addSongToList(newSong);
  document.getElementById('videoUrl').value = '';
}

async function fetchMediaDetails(url) {
  let mediaId, platform, trackTitle, mediaThumbnail;

  if ((mediaId = extractYoutubeId(url))) {
    platform = 'YouTube';
    mediaThumbnail = `https://img.youtube.com/vi/${mediaId}/0.jpg`;
    trackTitle = await getYoutubeVideoTitle(mediaId) || "YouTube Video";
  } else if ((mediaId = extractSpotifyId(url))) {
    platform = 'Spotify';
    const spotifyData = await getSpotifyTrackData(mediaId);
    if (!spotifyData) return null;
    trackTitle = spotifyData.trackTitle;
    mediaThumbnail = spotifyData.trackImage;
  } else if ((mediaId = extractDeezerId(url))) {
    platform = 'Deezer';
    trackTitle = "Deezer Track";
    mediaThumbnail = `https://cdns-images.dzcdn.net/images/cover/${mediaId}/250x250-000000-80-0-0.jpg`;
  } else if ((mediaId = extractSoundCloudId(url))) {
    platform = 'SoundCloud';
    trackTitle = "SoundCloud Track";
    mediaThumbnail = `https://i1.sndcdn.com/avatars-${mediaId.track}-large.jpg`;
  } else {
    return null;
  }

  return { url, title: trackTitle, platform, thumbnail: mediaThumbnail, category: "misc" };
}


function setImageOrFallback(img, thumbnailUrl, platform) {
  img.onerror = () => {
    img.src = getPlatformLogo(platform);
  };

  img.src = thumbnailUrl;
}

function getPlatformLogo(platform) {
  switch (platform) {
    case 'YouTube':
      return 'https://upload.wikimedia.org/wikipedia/commons/0/0c/YouTube_Logo_2015.png'; // Logo YouTube
    case 'Spotify':
      return 'https://upload.wikimedia.org/wikipedia/commons/6/67/Spotify_logo_with_text.svg'; // Logo Spotify
    case 'SoundCloud':
      //  return 'https://i1.sndcdn.com/avatars-000080650369-z4x6ty-t500x500.jpg'; // Logo SoundCloud
      return 'https://logospng.org/wp-content/uploads/soundcloud.png'; // Logo SoundCloud
    case 'Deezer':
      return 'https://upload.wikimedia.org/wikipedia/fr/c/ca/Logo_Deezer_2023.svg'; // Logo Deezer
    default:
      return 'https://via.placeholder.com/100'; // Logo générique si plateforme inconnue
  }
}

async function handleEditMedia(song, mediaItem, categoryElement, img, mediaLinkContainer) {
  const mediaLinkElement = mediaLinkContainer.querySelector('a');
  // Créer une nouvelle div pour les champs de modification
  const editForm = document.createElement('div');
  editForm.classList.add('d-flex', 'flex-column', 'w-75', 'inputs-container');

  // Créer les champs d'édition
  const inputFieldTitle = createInputField(song.title, 'mb-1');
  const inputFieldUrl = createInputField(song.url, 'mb-1');


  // Créer la liste déroulante pour les catégories
  const categorySelect = document.createElement('select');
  categorySelect.classList.add('form-select', 'form-control', 'w-75');
  categories.forEach(cat => {
    const option = document.createElement('option');
    option.value = cat.key;
    option.textContent = cat.value;
    if (cat.key === song.category) option.selected = true; // Sélectionner la catégorie actuelle
    categorySelect.appendChild(option);
  });

  // Ajouter les champs d'édition à la div
  editForm.append(inputFieldTitle, inputFieldUrl, categorySelect);

  // Créer les boutons Enregistrer et Annuler
  const saveButton = createButton("Enregistrer", 'btn-success');
  const cancelButton = createButton("Annuler", 'btn-secondary');

  // Masquer les boutons Modifier et Supprimer
  const editButton = mediaItem.querySelector(`#edit-btn-${song.id}`);
  const deleteButton = mediaItem.querySelector(`#delete-btn-${song.id}`);
  editButton.style.display = 'none';
  deleteButton.style.display = 'none';

  // Remplacer les éléments d'origine par la div de modification
  const mediaContent = mediaItem.querySelector('.d-flex');
  mediaContent.replaceChild(editForm, mediaLinkContainer);

  // Ajouter les boutons Enregistrer et Annuler
  const saveCancelButtons = document.createElement('div');
  saveCancelButtons.classList.add('d-flex', 'gap-2', 'justify-content-lg-end', 'justify-content-center', 'justify-content-start', 'save-cancel-container');
  saveCancelButtons.append(saveButton, cancelButton)
  mediaItem.append(saveCancelButtons);

  // Gestion de l'annulation
  cancelButton.addEventListener('click', () => {
    // Restaurer les éléments d'origine
    mediaContent.replaceChild(mediaLinkContainer, editForm);

    // Supprimer les boutons Enregistrer et Annuler
    saveButton.remove();
    cancelButton.remove();

    // Afficher à nouveau les boutons Modifier et Supprimer
    editButton.style.display = '';
    deleteButton.style.display = '';
  });

  // Gestion de l'enregistrement
  saveButton.addEventListener('click', async () => {
    const newTitle = inputFieldTitle.value.trim();
    const newUrl = inputFieldUrl.value.trim();
    const newCategorie = categorySelect.value;

    if (!newTitle || !newUrl) {
      alert("Veuillez remplir tous les champs.");
      return;
    }

    const updatedDetails = await fetchMediaDetails(newUrl);
    if (!updatedDetails) {
      alert("URL invalide ou plateforme non supportée !");
      return;
    }

    updatedDetails.id = song.id;
    updatedDetails.title = newTitle || song.title;
    updatedDetails.category = newCategorie || 'autre';

    // Mettre à jour la chanson dans la liste
    await songs.editSong(updatedDetails);

    // Mettre à jour les éléments d'origine avec les nouvelles valeurs
    mediaLinkElement.textContent = updatedDetails.title;
    mediaLinkElement.href = updatedDetails.url;

    // Mettre à jour la catégorie affichée
    //const categoryElement = mediaItem.querySelector('.badge');

    categoryElement.textContent = `${categories.find(cat => cat.key === updatedDetails.category)?.value || "Autre"}`;
    categoryElement.className = `ms-3 badge badge-${updatedDetails.category}`;

    // Restaurer les éléments d'origine
    mediaContent.replaceChild(mediaLinkContainer, editForm);

    // Supprimer les boutons Enregistrer et Annuler
    saveButton.remove();
    cancelButton.remove();

    // Afficher à nouveau les boutons Modifier et Supprimer
    editButton.style.display = '';
    deleteButton.style.display = '';
  });
}


function createButton(text, className) {
  const button = document.createElement('button');
  button.textContent = text;
  button.classList.add('btn', className);
  return button;
}

function createInputField(value, classes) {
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value;

  input.classList.add('form-control', 'w-75');

  if (typeof classes === 'string') {
    input.classList.add(...classes.split(' '));
  } else if (Array.isArray(classes)) {
    input.classList.add(...classes);
  }

  return input;
}


function addSongToList(song) {
  const mediaItem = document.createElement('li');
  mediaItem.classList.add('list-group-item', 'd-flex', 'flex-lg-row', 'flex-column', 'align-items-lg-center', 'mb-2');
  mediaItem.id = song.id;

  const mediaContent = document.createElement('div');
  mediaContent.classList.add('d-flex', 'align-items-center', 'flex-grow-1', 'mb-lg-0', 'mb-3', 'media-content');

  const img = document.createElement('img');
  img.src = song.thumbnail;
  img.alt = "Aperçu";
  img.classList.add('img-thumbnail', 'me-3');
  img.style.width = '100px';
  setImageOrFallback(img, song.thumbnail, song.platform);

  const mediaLinkContainer = document.createElement('div');
  mediaLinkContainer.classList.add('media-link-container', 'd-flex', 'flex-column'
    , 'flex-md-row', 'align-items-start', 'align-items-center'
  );
  const mediaLinkElement = document.createElement('a');
  mediaLinkElement.href = song.url;
  mediaLinkElement.target = "_blank";
  mediaLinkElement.textContent = `${song.title}`;
  mediaLinkElement.classList.add('btn', 'btn-link', 'px-0');
  mediaLinkContainer.appendChild(mediaLinkElement);

  // Affichage de la catégorie
  const categoryElement = document.createElement('span');
  categoryElement.textContent = `${categories.find(cat => cat.key === song.category)?.value || "Autre"}`;
  categoryElement.classList.add('ms-0', 'ms-md-3', 'badge', `badge-${song.category}`);
  mediaLinkContainer.appendChild(categoryElement);

  mediaContent.append(img, mediaLinkContainer);

  const actionButtons = document.createElement('div');
  actionButtons.classList.add('d-flex', 'gap-2', 'justify-content-lg-end', 'justify-content-center', 'justify-content-start', 'update-delete-container');

  const editButton = createButton("Modifier", 'btn-warning');
  editButton.id = `edit-btn-${song.id}`;
  editButton.addEventListener('click', () => handleEditMedia(song, mediaItem, categoryElement, img, mediaLinkContainer));

  const deleteButton = createButton("Supprimer", 'btn-danger');
  deleteButton.id = `delete-btn-${song.id}`;
  deleteButton.addEventListener('click', async () => {
    if (confirm("Voulez-vous vraiment supprimer cet élément ?")) {
      await songs.removeSong(song.id);
      mediaItem.remove();
    }
  });

  actionButtons.append(editButton, deleteButton);
  mediaItem.append(mediaContent, actionButtons);

  document.getElementById('playlist').appendChild(mediaItem);
}

export async function initPlaylist() {
  initMenu();
  initBanner();
  initFooter();

  const existingSongs = await songs.getSongs();
  existingSongs.forEach(addSongToList);

  document.getElementById('addVideoBtn').addEventListener('click', handleAddMedia);
}